import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { centsToInput, maskReaisInput, parseReaisInput } from "@/lib/format";
import type { ContratoClt } from "@/data/folha";
import type { User } from "@/data/mock";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface Props {
  usuario: User;
  open: boolean;
  onClose: () => void;
}

const erro = (err: unknown, padrao: string) => toast.error(err instanceof Error ? err.message : padrao, { duration: 10_000 });

const inteiro = (s: string) => {
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/** R$ digitado com a máscara → reais (number). Vazio = 0. */
const reais = (s: string) => (s.trim() ? parseReaisInput(s) : 0);

// ---------------------------------------------------------------------------
// Contrato
// ---------------------------------------------------------------------------

const ContratoAba = ({ usuario, onSalvo }: { usuario: User; onSalvo: () => void }) => {
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [f, setF] = useState({
    admissao: "",
    cargo: "",
    cbo: "",
    dependentesIrrf: "0",
    filhos: "0",
    gratificacao: "0",
    vt: "",
    fimExperiencia: "",
    desligamento: "",
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    api
      .get<ContratoClt | null>(`/clt/contratos/${usuario.id}`)
      .then((c) => {
        if (!c) return;
        setF({
          admissao: c.admissao ?? "",
          cargo: c.cargo ?? "",
          cbo: c.cbo ?? "",
          dependentesIrrf: String(c.dependentesIrrf),
          filhos: String(c.filhosSalarioFamilia),
          gratificacao: String(c.gratificacaoPct / 100),
          vt: c.valeTransporteMensal ? centsToInput(c.valeTransporteMensal) : "",
          fimExperiencia: c.fimExperiencia ?? "",
          desligamento: c.desligamento ?? "",
        });
      })
      .catch((e) => erro(e, "Erro ao carregar o contrato"))
      .finally(() => setCarregando(false));
  }, [usuario.id]);

  const salvar = async () => {
    if (!f.admissao) {
      toast.error("A data de admissão é obrigatória para calcular a folha.");
      return;
    }
    setSalvando(true);
    try {
      await api.put(`/clt/contratos/${usuario.id}`, {
        admissao: f.admissao,
        cargo: f.cargo.trim() || null,
        cbo: f.cbo.trim() || null,
        dependentesIrrf: inteiro(f.dependentesIrrf),
        filhosSalarioFamilia: inteiro(f.filhos),
        gratificacaoPct: Number(f.gratificacao.replace(",", ".")) || 0,
        valeTransporteMensal: reais(f.vt),
        fimExperiencia: f.fimExperiencia || null,
        desligamento: f.desligamento || null,
      });
      toast.success("Contrato salvo — folhas em rascunho recalculadas");
      onSalvo();
    } catch (e) {
      erro(e, "Erro ao salvar o contrato");
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) return <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-muted-foreground" />;

  const campo = (id: string, rotulo: string, filho: React.ReactNode, ajuda?: string) => (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{rotulo}</Label>
      {filho}
      {ajuda && <p className="text-xs text-muted-foreground">{ajuda}</p>}
    </div>
  );

  return (
    <div className="space-y-4">
      {usuario.salarioFixo == null && (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300">
          Sem salário fixo cadastrado: informe em <strong>Editar cadastro</strong> para a folha ser calculada.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {campo("clt-adm", "Admissão *", <Input id="clt-adm" type="date" value={f.admissao} onChange={(e) => set("admissao", e.target.value)} />)}
        {campo("clt-cargo", "Cargo", <Input id="clt-cargo" value={f.cargo} onChange={(e) => set("cargo", e.target.value)} />)}
        {campo("clt-cbo", "CBO", <Input id="clt-cbo" value={f.cbo} onChange={(e) => set("cbo", e.target.value)} inputMode="numeric" />)}
        {campo(
          "clt-dep",
          "Dependentes de IR",
          <Input id="clt-dep" value={f.dependentesIrrf} onChange={(e) => set("dependentesIrrf", e.target.value.replace(/\D/g, ""))} inputMode="numeric" />,
        )}
        {campo(
          "clt-filhos",
          "Filhos p/ salário-família",
          <Input id="clt-filhos" value={f.filhos} onChange={(e) => set("filhos", e.target.value.replace(/\D/g, ""))} inputMode="numeric" />,
          "Até 14 anos ou inválidos.",
        )}
        {campo(
          "clt-grat",
          "Gratificação de função (%)",
          <Input id="clt-grat" value={f.gratificacao} onChange={(e) => set("gratificacao", e.target.value.replace(/[^\d,.]/g, ""))} inputMode="decimal" />,
          "Sobre o salário base (cargo de confiança). 0 se não tem.",
        )}
        {campo(
          "clt-vt",
          "Vale-transporte (custo mensal)",
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
            <Input id="clt-vt" value={f.vt} onChange={(e) => set("vt", maskReaisInput(e.target.value))} placeholder="0,00" inputMode="numeric" className="pl-9 tabular-nums" />
          </div>,
          "Desconta até 6% do salário. Vazio = não optante.",
        )}
        {campo("clt-exp", "Fim do contrato de experiência", <Input id="clt-exp" type="date" value={f.fimExperiencia} onChange={(e) => set("fimExperiencia", e.target.value)} />)}
        {campo(
          "clt-desl",
          "Desligamento (último dia trabalhado)",
          <Input id="clt-desl" type="date" value={f.desligamento} onChange={(e) => set("desligamento", e.target.value)} />,
          "A folha do mês paga só até esta data. A rescisão é calculada fora do sistema.",
        )}
      </div>
      <div className="flex justify-end">
        <Button onClick={() => void salvar()} disabled={salvando}>
          {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Salvar contrato
        </Button>
      </div>
    </div>
  );
};

/** Dados contratuais do CLT usados no cálculo do holerite. */
export const DadosCltModal = ({ usuario, open, onClose }: Props) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Dados CLT — {usuario.name}</DialogTitle>
        <DialogDescription>
          Usados no cálculo do holerite. Toda alteração recalcula as folhas em rascunho.
        </DialogDescription>
      </DialogHeader>
      <ContratoAba usuario={usuario} onSalvo={() => undefined} />
    </DialogContent>
  </Dialog>
);
